import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Form, Input } from "reactstrap";
import { MIXED } from "../../shared/bulkFields";
import { hasSlotPlaceholder, renderSlotTemplate } from "../treeReducer";
import { Section } from "./FieldRow";
import InferenceMachineAutocomplete from "./InferenceMachineAutocomplete";
import useBulkForm from "./useBulkForm";
import {
  BulkFieldRow,
  BulkFormHeader,
  BulkFormSummary,
  BulkSelect,
  TriStateCheckbox,
  VARIES_LABEL,
} from "./bulkControls";

const STREAM_QUALITIES = [
  { value: "MAIN", label: "MAIN" },
  { value: "SUB", label: "SUB" },
];

const PROTOCOLS = [
  { value: "TCP", label: "TCP" },
  { value: "UDP", label: "UDP" },
];

function fkId(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "object") return value.id ?? null;
  return value;
}

/**
 * Common per-slot suffix patterns seen in production, offered as one-click
 * presets. Clicking one fills the template input; the operator adjusts and
 * applies explicitly.
 */
const TEMPLATE_PRESETS = [
  { label: "Hikvision", template: "/Streaming/Channels/{slot}02" },
  { label: "Dahua", template: "/cam/realmonitor?channel={slot}&subtype=1" },
  { label: "TVT", template: "/?chID={slot}&streamType=sub&linkType=tcp" },
  { label: "Uniview", template: "/unicast/c{slot}/s1/live" },
  { label: "Reolink", template: "/h264Preview_{slot:2}_sub" },
];

/**
 * One suffix input for the whole selection.
 *
 * Plain text applies the same suffix to every selected camera (BULK_UPDATE);
 * text carrying `{slot}` / `{slot:N}` is rendered against each camera's own
 * slot_number instead (BULK_APPLY_TEMPLATE). One input, one Apply button, and
 * a line underneath saying which of the two will happen.
 */
function SuffixField({ cameras, publicIds, dispatch }) {
  const [text, setText] = useState("");
  const isTemplate = hasSlotPlaceholder(text);
  const firstSlot = cameras.length > 0 ? cameras[0].slot_number ?? 0 : 0;
  const count = cameras.length;

  // What the selection currently holds (working values, pending edits included),
  // so an applied template stays visible instead of vanishing into a blank input.
  const suffixes = cameras.map((camera) => camera.rtsp_url_suffix || "");
  const allSame = suffixes.every((suffix) => suffix === suffixes[0]);
  const shown = cameras.slice(0, 3);
  const currentLine = allSame
    ? `Current: ${suffixes[0] || "(empty)"} — same for the whole selection`
    : `Current: ${shown
        .map((camera) => `slot ${camera.slot_number} → ${camera.rtsp_url_suffix || "(empty)"}`)
        .join(", ")}${count > 3 ? `, +${count - 3} more` : ""}`;
  const currentTitle = cameras
    .map((camera) => `slot ${camera.slot_number} → ${camera.rtsp_url_suffix || "(empty)"}`)
    .join("\n");

  const apply = () => {
    if (isTemplate) {
      dispatch({
        type: "BULK_APPLY_TEMPLATE",
        entityType: "camera",
        publicIds,
        field: "rtsp_url_suffix",
        template: text,
      });
    } else {
      dispatch({ type: "BULK_UPDATE", entityType: "camera", publicIds, patch: { rtsp_url_suffix: text || null } });
    }
  };

  let hint;
  if (isTemplate) {
    hint = `Each camera gets its own suffix — e.g. slot ${firstSlot} → ${renderSlotTemplate(text, firstSlot)}`;
  } else if (text) {
    hint = `All ${count} cameras get exactly this suffix.`;
  } else {
    hint = "Type a suffix, or use {slot} to insert each camera's slot number.";
  }

  return (
    <div>
      <small className="d-block mb-1" style={{ fontSize: "0.72rem", color: "#bcc1c8" }} title={currentTitle}>
        {currentLine}
      </small>
      <div className="d-flex align-items-center" style={{ gap: 6 }}>
        <Input
          bsSize="sm"
          aria-label="RTSP URL suffix"
          placeholder="/cam/realmonitor?channel={slot}&subtype=1"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button size="sm" color="info" outline disabled={!text} onClick={apply} style={{ whiteSpace: "nowrap" }}>
          Apply suffix
        </Button>
      </div>
      <small className={`d-block mt-1 ${isTemplate ? "text-info" : "text-muted"}`} style={{ fontSize: "0.72rem" }}>
        {hint}
      </small>
      <div className="mt-1 d-flex align-items-center flex-wrap" style={{ gap: 4 }}>
        <small className="text-muted" style={{ fontSize: "0.7rem" }}>
          Common brand patterns:
        </small>
        {TEMPLATE_PRESETS.map((preset) => (
          <Button
            key={preset.label}
            size="sm"
            color="secondary"
            outline
            className="py-0 px-1"
            style={{ fontSize: "0.7rem" }}
            title={preset.template}
            onClick={() => setText(preset.template)}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

SuffixField.propTypes = {
  cameras: PropTypes.arrayOf(PropTypes.shape({ slot_number: PropTypes.number })).isRequired,
  publicIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  dispatch: PropTypes.func.isRequired,
};

/**
 * Edits the bulk-shared camera fields across a selection. Identity fields
 * (slot_number) and the routing fields with no bulk demand (alert_channel,
 * depth_of_field) stay in the single-camera form.
 */
export default function BulkEditCameraForm({ cameras, serverCameras, dispatch }) {
  const form = useBulkForm(cameras, serverCameras, "camera", dispatch);
  const machine = form.valueOf("inference_machine");

  return (
    <Form>
      <BulkFormHeader count={cameras.length} noun={cameras.length === 1 ? "camera" : "cameras"} />

      <Section title="Feed">
        <BulkFieldRow
          selectedCount={cameras.length}
          label="Stream quality"
          modified={form.isModified("stream_quality")}
          onRevert={form.revertField("stream_quality")}
        >
          <BulkSelect
            label="Stream quality"
            value={form.valueOf("stream_quality")}
            options={STREAM_QUALITIES}
            onChange={form.setField("stream_quality")}
          />
        </BulkFieldRow>

        <BulkFieldRow
          selectedCount={cameras.length}
          label="RTSP URL suffix"
          modified={form.isModified("rtsp_url_suffix")}
          onRevert={form.revertField("rtsp_url_suffix")}
        >
          <SuffixField cameras={cameras} publicIds={form.publicIds} dispatch={dispatch} />
        </BulkFieldRow>

        <BulkFieldRow
          selectedCount={cameras.length}
          label="Protocol"
          modified={form.isModified("protocol")}
          onRevert={form.revertField("protocol")}
        >
          <BulkSelect
            label="Protocol"
            value={form.valueOf("protocol")}
            options={PROTOCOLS}
            onChange={form.setField("protocol")}
          />
        </BulkFieldRow>
      </Section>

      <Section title="Inference routing">
        <BulkFieldRow
          selectedCount={cameras.length}
          label="Inference machine"
          hint={machine === MIXED ? "Selected cameras use different machines" : null}
          modified={form.isModified("inference_machine")}
          onRevert={form.revertField("inference_machine")}
        >
          {/* The autocomplete resolves one hostname from one id, so a divergent
              selection has nothing to show: it renders empty with the hint above,
              and picking a machine assigns it to every selected camera. */}
          <InferenceMachineAutocomplete
            value={machine === MIXED ? null : fkId(machine)}
            onChange={form.setField("inference_machine")}
          />
          {machine === MIXED && (
            <small className="text-muted font-italic" style={{ fontSize: "0.75rem" }}>
              {VARIES_LABEL}
            </small>
          )}
        </BulkFieldRow>
      </Section>

      <Section title="State">
        <BulkFieldRow
          selectedCount={cameras.length}
          label="Active"
          modified={form.isModified("is_active")}
          onRevert={form.revertField("is_active")}
        >
          <TriStateCheckbox label="Active" value={form.valueOf("is_active")} onChange={form.setField("is_active")} />
        </BulkFieldRow>
      </Section>

      <BulkFormSummary dirtyCount={form.dirtyCount} count={cameras.length} />
    </Form>
  );
}

BulkEditCameraForm.propTypes = {
  cameras: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string.isRequired })).isRequired,
  serverCameras: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string })).isRequired,
  dispatch: PropTypes.func.isRequired,
};
